import { brandConfig, features } from '@/config/brands'
import { createDebugger } from '@/utils/debug'

const debug = createDebugger('ad')

const GPT_AD_CONFIG = brandConfig.gptAd
const AD_HOST_KEYWORD = GPT_AD_CONFIG?.hostKeyword || ''
const isScrollDomain = !AD_HOST_KEYWORD || AD_HOST_KEYWORD.split(',').map(k => k.trim()).some(k => location.hostname.includes(k))
const GPT_AD_SLOTS = isScrollDomain && GPT_AD_CONFIG?.hostMatchedSlots?.length
  ? GPT_AD_CONFIG.hostMatchedSlots
  : GPT_AD_CONFIG?.slots || []
const SLOT_COUNT = GPT_AD_SLOTS.length
// 每批预加载两个广告；广告位仍按全局序号依次轮换。
const PRELOAD_BATCH_SIZE = 2
const AD_ENABLED = !!(features?.enableAdSense && GPT_AD_CONFIG?.charThreshold && SLOT_COUNT)
const INTERSTITIAL_ENABLED = !!(features?.enableInterstitial && GPT_AD_CONFIG?.interstitialUnitPath)
const GPT_SCRIPT_SRC = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js'
const GPT_SCRIPT_ID = 'gpt-script'

debug('域名%s关键字，使用%s广告位 (%d个)', isScrollDomain ? '匹配' : '不匹配', isScrollDomain ? 'scroll' : '非scroll', SLOT_COUNT)

let gptInited = false
const slotMap = new Map<string, GoogletagSlot>()
let nextPreloadOrder = 1
// 预加载窗口：首个可见广告与紧随的一个广告一起请求；只有窗口末尾广告进入可见范围，
// 才继续放开下一批两个。这样既保持广告位按全局序号使用，也避免中间广告可见时提前扩窗。
let preloadWindowStart = Number.POSITIVE_INFINITY
let preloadWindowEnd = 0
const preloadWaiters: Array<{ order: number; resolve: () => void }> = []
const initialPreloadCandidates = new Set<number>()
let initialPreloadFrame: number | null = null
let globalAdIndex = 0
let interstitialSlot: GoogletagSlot | null = null
let interstitialRequestedHandler: ((event: any) => void) | null = null
let interstitialResponseHandler: ((event: any) => void) | null = null
let interstitialRenderHandler: ((event: any) => void) | null = null
let interstitialOnloadHandler: ((event: any) => void) | null = null

export function getNextAdIndex(): number {
  return globalAdIndex++
}

export function resetAdIndex() {
  globalAdIndex = 0
}

function getGpt(): Googletag {
  return window.googletag as Googletag
}

function ensureGptScript() {
  if (getGpt().apiReady) {
    debug('GPT SDK 已就绪')
    return
  }

  const existingScript = document.getElementById(GPT_SCRIPT_ID)
    || document.querySelector(`script[src="${GPT_SCRIPT_SRC}"]`)
  if (existingScript) {
    debug('检测到 GPT SDK 脚本，等待加载完成')
    return
  }

  const script = document.createElement('script')
  script.id = GPT_SCRIPT_ID
  script.async = true
  script.src = GPT_SCRIPT_SRC
  script.crossOrigin = 'anonymous'
  script.onload = () => debug('GPT SDK 脚本加载完成')
  script.onerror = () => debug.error('GPT SDK 脚本加载失败，广告请求不会执行')
  document.head.appendChild(script)
  debug('已动态加载 GPT SDK 脚本')
}

function ensureGpt() {
  if (gptInited)
    return
  gptInited = true
  window.googletag = window.googletag || { cmd: [] }
  ensureGptScript()
  getGpt().cmd.push(() => {
    getGpt().pubads().enableSingleRequest()
    getGpt().pubads().enableAsyncRendering()
    getGpt().enableServices()
    debug('GPT 初始化完成')
  })
}

/** 注册并请求 GPT 托管普通插屏，展示时机由 GPT 决定。 */
function prepareInterstitial() {
  debug('注册普通插屏 enabled=%s unit=%s', INTERSTITIAL_ENABLED, GPT_AD_CONFIG?.interstitialUnitPath || 'none')
  if (!INTERSTITIAL_ENABLED) {
    debug.warn('普通插屏开关或广告位未配置，跳过注册')
    return
  }
  if (interstitialSlot) {
    debug('普通插屏广告位已存在，跳过重复注册')
    return
  }

  ensureGpt()
  getGpt().cmd.push(() => {
    const gpt = getGpt()
    const slot = gpt.defineOutOfPageSlot(GPT_AD_CONFIG!.interstitialUnitPath!, gpt.enums.OutOfPageFormat.INTERSTITIAL)
    if (!slot) {
      debug.warn('普通插屏不受当前页面或广告位支持')
      return
    }

    interstitialSlot = slot
    interstitialRequestedHandler = (event: any) => {
      if (event.slot === slot)
        debug('普通插屏 GPT 请求已发出')
    }
    interstitialResponseHandler = (event: any) => {
      if (event.slot === slot)
        debug('普通插屏 GPT 已收到广告响应')
    }
    interstitialRenderHandler = (event: any) => {
      if (event.slot === slot)
        debug('普通插屏渲染完成 empty=%s size=%s', event.isEmpty, event.size || 'unknown')
    }
    interstitialOnloadHandler = (event: any) => {
      if (event.slot === slot)
        debug('普通插屏素材加载完成')
    }
    gpt.pubads().addEventListener('slotRequested', interstitialRequestedHandler)
    gpt.pubads().addEventListener('slotResponseReceived', interstitialResponseHandler)
    gpt.pubads().addEventListener('slotRenderEnded', interstitialRenderHandler)
    gpt.pubads().addEventListener('slotOnload', interstitialOnloadHandler)
    slot.addService(gpt.pubads())
    debug('普通插屏广告位已注册，调用 GPT display')
    gpt.display(slot)
  })
}

function destroyInterstitial() {
  const slot = interstitialSlot
  const requestedHandler = interstitialRequestedHandler
  const responseHandler = interstitialResponseHandler
  const renderHandler = interstitialRenderHandler
  const onloadHandler = interstitialOnloadHandler
  interstitialSlot = null
  if (!slot || !window.googletag)
    return

  getGpt().cmd.push(() => {
    if (requestedHandler)
      getGpt().pubads().removeEventListener('slotRequested', requestedHandler)
    if (responseHandler)
      getGpt().pubads().removeEventListener('slotResponseReceived', responseHandler)
    if (renderHandler)
      getGpt().pubads().removeEventListener('slotRenderEnded', renderHandler)
    if (onloadHandler)
      getGpt().pubads().removeEventListener('slotOnload', onloadHandler)
    getGpt().destroySlots([slot])
  })
  interstitialRequestedHandler = null
  interstitialResponseHandler = null
  interstitialRenderHandler = null
  interstitialOnloadHandler = null
}

export function useGptAdManager() {
  const threshold = GPT_AD_CONFIG?.charThreshold || 500

  function getAdSlotForIndex(index: number) {
    return GPT_AD_SLOTS[index % SLOT_COUNT]
  }

  function registerPreloadItem() {
    return nextPreloadOrder++
  }

  function isWithinPreloadWindow(order: number) {
    return order >= preloadWindowStart && order <= preloadWindowEnd
  }

  function waitForPreloadTurn(order: number): Promise<void> {
    if (isWithinPreloadWindow(order))
      return Promise.resolve()

    debug('等待预加载窗口 globalOrder=%s start=%s end=%s', order, preloadWindowStart, preloadWindowEnd)

    return new Promise((resolve) => {
      preloadWaiters.push({ order, resolve })
    })
  }

  // 首屏多个隐藏占位容器的可见回调顺序并不稳定。汇总同一轮回调并选最小序号，
  // 确保标题下固定广告 (#1) 优先启动首批，而不是被 #2 等后续广告抢先启动。
  function startPreloadIfNeeded(order: number) {
    if (preloadWindowEnd !== 0)
      return false
    initialPreloadCandidates.add(order)
    if (initialPreloadFrame !== null)
      return true

    initialPreloadFrame = requestAnimationFrame(() => {
      initialPreloadFrame = null
      if (preloadWindowEnd !== 0)
        return
      const firstOrder = Math.min(...initialPreloadCandidates)
      initialPreloadCandidates.clear()
      noteAdVisible(firstOrder)
    })
    return true
  }

  // 首次可见时放开当前广告及后续一个；之后仅窗口末尾广告可见时，继续放开下一批两个。
  // 广告序号/广告位在创建时已确定，这里只决定何时允许请求，不会改变 detail1、detail2… 的顺序。
  function noteAdVisible(order: number) {
    let changed = false
    if (order < preloadWindowStart) {
      preloadWindowStart = order
      changed = true
    }
    if (preloadWindowEnd === 0) {
      preloadWindowEnd = order + PRELOAD_BATCH_SIZE - 1
      changed = true
    }
    else if (order === preloadWindowEnd) {
      // 窗口末尾可见，追加下一整批。例如 #1/#2 已放开，看到 #2 后放开 #3/#4。
      preloadWindowEnd += PRELOAD_BATCH_SIZE
      changed = true
    }
    else if (order > preloadWindowEnd) {
      // 用户直接跳过中间广告时，放开跳转位置及其后的一个广告，保证当前广告可被请求。
      preloadWindowEnd = order + PRELOAD_BATCH_SIZE - 1
      changed = true
    }
    if (!changed)
      return

    debug('扩展预加载窗口 visibleOrder=%s start=%s end=%s', order, preloadWindowStart, preloadWindowEnd)

    for (let i = preloadWaiters.length - 1; i >= 0; i--) {
      const waiter = preloadWaiters[i]
      if (!isWithinPreloadWindow(waiter.order))
        continue
      preloadWaiters.splice(i, 1)
      waiter.resolve()
    }
  }

  // 批次末尾广告空填充/超时时，由组件在用户后续滚动到该位置后调用，放开下一批。
  function noteAdFailed(order: number) {
    if (order !== preloadWindowEnd) {
      debug('广告填充失败，不扩批 globalOrder=%s currentEnd=%s', order, preloadWindowEnd)
      return
    }
    debug('批次末尾广告填充失败，放开下一批 globalOrder=%s currentEnd=%s', order, preloadWindowEnd)
    noteAdVisible(order)
  }

  function displayAd(divId: string, unitPath: string, slotName = ''): Promise<boolean> {
    if (!AD_ENABLED)
      return Promise.resolve(false)

    ensureGpt()

    debug('预加载开始 slot=%s div=%s', slotName, divId)

    return new Promise((resolve) => {
      let done = false
      let handler: ((event: any) => void) | null = null
      let timer: ReturnType<typeof setTimeout>

      const finish = (filled: boolean) => {
        if (done)
          return
        done = true
        clearTimeout(timer)
        if (handler)
          getGpt().pubads().removeEventListener('slotRenderEnded', handler)
        resolve(filled)
      }

      timer = setTimeout(() => {
        if (!done) {
          debug.warn('超时 (8s) 未填充 slot=%s div=%s', slotName, divId)
          finish(false)
        }
      }, 8000)

      getGpt().cmd.push(() => {
        const slot = getGpt().defineSlot(unitPath, GPT_AD_CONFIG?.size || [300, 250], divId)
        if (!slot) {
          if (!done) {
            debug.warn('defineSlot 失败 slot=%s div=%s', slotName, divId)
            finish(false)
          }
          return
        }
        slotMap.set(divId, slot)
        slot.addService(getGpt().pubads())

        handler = (event: any) => {
          if (event.slot.getSlotElementId() === divId && !done) {
            const filled = !event.isEmpty
            if (filled)
              debug('填充成功 slot=%s div=%s', slotName, divId)
            else
              debug.warn('填充失败（空广告） slot=%s div=%s', slotName, divId)
            finish(filled)
          }
        }
        getGpt().pubads().addEventListener('slotRenderEnded', handler)

        getGpt().display(divId)
      })
    })
  }

  function destroyAd(divId: string) {
    const slot = slotMap.get(divId)
    if (!slot || !window.googletag)
      return

    getGpt().cmd.push(() => {
      getGpt().destroySlots([slot])
      slotMap.delete(divId)
    })
  }

  function resetGptAdState() {
    preloadWaiters.splice(0).forEach(w => w.resolve())
    initialPreloadCandidates.clear()
    if (initialPreloadFrame !== null) {
      cancelAnimationFrame(initialPreloadFrame)
      initialPreloadFrame = null
    }
    slotMap.clear()
    nextPreloadOrder = 1
    preloadWindowStart = Number.POSITIVE_INFINITY
    preloadWindowEnd = 0
    globalAdIndex = 0
    debug('广告状态已重置')
  }

  return {
    getAdSlotForIndex,
    registerPreloadItem,
    waitForPreloadTurn,
    startPreloadIfNeeded,
    noteAdVisible,
    noteAdFailed,
    displayAd,
    destroyAd,
    resetGptAdState,
    AD_ENABLED,
    prepareInterstitial,
    destroyInterstitial,
    threshold,
    adSize: GPT_AD_CONFIG?.size || [300, 250],
    SLOT_COUNT,
    isScrollDomain,
    PRELOAD_BATCH_SIZE,
  }
}
